/** Path-oriented PHP/MySQL data client kept compatible with the app's existing record model. */
export type Unsubscribe = () => void;
export type Database = { readonly driver: 'hostinger-php-sql' };
export type DatabaseReference = { readonly database: Database; readonly path: string; readonly key: string | null; get(): Promise<DataSnapshot> };
export type ThenableDatabaseReference = DatabaseReference & PromiseLike<DatabaseReference>;
export type Query = DatabaseReference & { readonly constraints: QueryState };
type QueryState = {
  orderByChild?: string;
  orderByKey?: boolean;
  equalTo?: unknown;
  hasEqualTo?: boolean;
  limitToFirst?: number;
  limitToLast?: number;
};
type QueryConstraint = { readonly kind: keyof QueryState; readonly value?: unknown };

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');
const TOKEN_KEY = 'nutrisync_access_token';
const listeners = new Map<string, Set<Unsubscribe>>();

export class DataSnapshot {
  constructor(
    private readonly value: unknown,
    readonly key: string | null,
    readonly ref: DatabaseReference,
    readonly version = 0,
  ) {}

  exists(): boolean { return this.value !== null && this.value !== undefined && (typeof this.value !== 'object' || Object.keys(this.value as object).length > 0); }
  val(): any { return this.exists() ? this.value : null; }
  size(): number { return this.value && typeof this.value === 'object' ? Object.keys(this.value).length : 0; }
  numChildren(): number { return this.size(); }
  child(path: string): DataSnapshot {
    const childPath = joinPath(this.ref.path, path);
    const value = readLocalPath(this.value, path);
    return new DataSnapshot(value, lastSegment(childPath), makeReference(this.ref.database, childPath));
  }
  forEach(action: (child: DataSnapshot) => boolean | void): boolean {
    if (!this.value || typeof this.value !== 'object') return false;
    for (const [key, child] of Object.entries(this.value as Record<string, unknown>)) {
      const childPath = joinPath(this.ref.path, key);
      if (action(new DataSnapshot(child, key, makeReference(this.ref.database, childPath)))) return true;
    }
    return false;
  }
}

function lastSegment(path: string): string | null {
  const parts = path.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : null;
}
function joinPath(parent: string, child: string): string {
  return [parent, child].filter(Boolean).join('/').replace(/\/{2,}/g, '/');
}
function readLocalPath(value: unknown, path: string): unknown {
  return path.split('/').filter(Boolean).reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') return null;
    return (current as Record<string, unknown>)[key] ?? null;
  }, value);
}
function makeReference(database: Database, path: string): DatabaseReference {
  const normalized = path.split('/').filter(Boolean).join('/');
  const reference: DatabaseReference = { database, path: normalized, key: lastSegment(normalized), get: () => get(reference) };
  return reference;
}
function asReference(value: DatabaseReference | Query): DatabaseReference {
  return makeReference(value.database, value.path);
}

export function createPhpDatabase(): Database { return { driver: 'hostinger-php-sql' }; }
export function ref(database: Database, path = ''): DatabaseReference {
  const normalized = path.split('/').filter(Boolean).join('/');
  return makeReference(database, normalized);
}
export function child(parent: DatabaseReference | Query, path: string): DatabaseReference {
  const normalized = joinPath(parent.path, path);
  return makeReference(parent.database, normalized);
}
export function orderByChild(path: string): QueryConstraint { return { kind: 'orderByChild', value: path }; }
export function orderByKey(): QueryConstraint { return { kind: 'orderByKey', value: true }; }
export function equalTo(value: unknown): QueryConstraint { return { kind: 'equalTo', value }; }
export function limitToFirst(value: number): QueryConstraint { return { kind: 'limitToFirst', value }; }
export function limitToLast(value: number): QueryConstraint { return { kind: 'limitToLast', value }; }
export function query(base: DatabaseReference | Query, ...constraints: QueryConstraint[]): Query {
  const state: QueryState = { ...('constraints' in base ? base.constraints : {}) };
  for (const constraint of constraints) {
    (state as any)[constraint.kind] = constraint.value;
    if (constraint.kind === 'equalTo') state.hasEqualTo = true;
  }
  return { ...asReference(base), constraints: state };
}

function token(): string | null {
  return typeof window === 'undefined' ? null : window.sessionStorage.getItem(TOKEN_KEY);
}
function endpoint(path: string, state: QueryState = {}): string {
  const params = new URLSearchParams({ action: 'get', path });
  if (state.orderByChild) params.set('orderByChild', state.orderByChild);
  if (state.orderByKey) params.set('orderByKey', '1');
  if (state.hasEqualTo) {
    params.set('hasEqualTo', '1');
    params.set('equalTo', JSON.stringify(state.equalTo));
  }
  if (state.limitToFirst !== undefined) params.set('limitToFirst', String(state.limitToFirst));
  if (state.limitToLast !== undefined) params.set('limitToLast', String(state.limitToLast));
  return `${API_BASE}/data.php?${params.toString()}`;
}
async function request(path: string, state: QueryState = {}): Promise<{ value: unknown; version: number }> {
  const accessToken = token();
  const isPublicLandingRead = path.split('/').filter(Boolean)[0] === 'landingContent';
  if (!accessToken && !isPublicLandingRead) throw new Error('Sua sessão NutriSync expirou. Entre novamente.');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const response = await fetch(endpoint(path, state), {
    headers,
    cache: 'no-store',
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success) throw new Error(result?.error || 'Não foi possível carregar os dados.');
  return { value: result.data?.value ?? null, version: Number(result.data?.version || 0) };
}
async function write(action: string, path: string, value?: unknown, extra: Record<string, unknown> = {}): Promise<any> {
  const accessToken = token();
  if (!accessToken) throw new Error('Sua sessão NutriSync expirou. Entre novamente.');
  const response = await fetch(`${API_BASE}/data.php?action=${encodeURIComponent(action)}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ path, ...(value === undefined ? {} : { value }), ...extra }),
    cache: 'no-store',
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success) throw new Error(result?.error || 'Não foi possível salvar os dados.');
  return result.data;
}

export async function get(source: DatabaseReference | Query): Promise<DataSnapshot> {
  const { value, version } = await request(source.path, 'constraints' in source ? source.constraints : {});
  const refValue = asReference(source);
  return new DataSnapshot(value, refValue.key, refValue, version);
}
export async function set(target: DatabaseReference | Query, value: unknown): Promise<void> {
  await write('set', target.path, value);
}
export async function update(target: DatabaseReference | Query, values: Record<string, unknown>): Promise<void> {
  await write('update', target.path, values);
}
export async function remove(target: DatabaseReference | Query): Promise<void> {
  await write('remove', target.path);
}
export function push(parent: DatabaseReference | Query, value?: unknown): ThenableDatabaseReference {
  const childRef = child(parent, `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 13)}`);
  const writePromise = value === undefined ? Promise.resolve() : set(childRef, value);
  // Keep synchronous access to .key while making `await push(ref, value)` wait for MySQL.
  void writePromise.catch(() => undefined);
  const reference = {
    ...childRef,
    then<TResult1 = DatabaseReference, TResult2 = never>(
      onfulfilled?: ((value: DatabaseReference) => TResult1 | PromiseLike<TResult1>) | null,
      onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
    ): PromiseLike<TResult1 | TResult2> {
      return writePromise.then(() => onfulfilled ? onfulfilled(childRef) : childRef as unknown as TResult1, onrejected ?? undefined);
    },
  };
  return reference;
}
export function serverTimestamp(): Record<string, true> { return { __nutriSyncServerTimestamp: true }; }

export async function runTransaction(
  target: DatabaseReference | Query,
  updateFn: (currentData: any) => unknown,
  _options?: { applyLocally?: boolean },
): Promise<{ committed: boolean; snapshot: DataSnapshot }> {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const snapshot = await get(asReference(target));
    const nextValue = updateFn(snapshot.val());
    if (nextValue === undefined) return { committed: false, snapshot };
    const result = await write('transaction', target.path, nextValue, { expectedVersion: snapshot.version });
    if (result?.committed) return { committed: true, snapshot: new DataSnapshot(nextValue, target.key, asReference(target), Number(result.version || snapshot.version + 1)) };
  }
  throw new Error('O registro foi alterado simultaneamente. Atualize a tela e tente novamente.');
}

export function onValue(
  source: DatabaseReference | Query,
  callback: (snapshot: DataSnapshot) => void,
  cancelOrOptions?: ((error: Error) => void) | { onlyOnce?: boolean },
  _options?: { onlyOnce?: boolean },
): Unsubscribe {
  const refValue = asReference(source);
  let stopped = false;
  let last = '';
  let timer: ReturnType<typeof setTimeout> | null = null;
  const errorCallback = typeof cancelOrOptions === 'function' ? cancelOrOptions : undefined;
  const onlyOnce = typeof cancelOrOptions === 'object' && !!cancelOrOptions.onlyOnce;
  const poll = async () => {
    if (stopped) return;
    try {
      const snapshot = await get(source);
      const serialized = JSON.stringify(snapshot.val());
      if (serialized !== last) {
        last = serialized;
        callback(snapshot);
      }
      if (onlyOnce) stop();
    } catch (error) {
      if (errorCallback) errorCallback(error instanceof Error ? error : new Error(String(error)));
    }
    if (!stopped) timer = setTimeout(poll, 5000);
  };
  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (timer) clearTimeout(timer);
    listeners.get(refValue.path)?.delete(stop);
  };
  if (!listeners.has(refValue.path)) listeners.set(refValue.path, new Set());
  listeners.get(refValue.path)!.add(stop);
  void poll();
  return stop;
}

export function onChildAdded(
  source: DatabaseReference | Query,
  callback: (snapshot: DataSnapshot, previousChildName?: string | null) => void,
  cancelCallback?: (error: Error) => void,
): Unsubscribe {
  const seen = new Set<string>();
  return onValue(source, (snapshot) => {
    snapshot.forEach((childSnapshot) => {
      if (childSnapshot.key && !seen.has(childSnapshot.key)) {
        seen.add(childSnapshot.key);
        callback(childSnapshot, null);
      }
      return false;
    });
  }, cancelCallback);
}

export function off(source: DatabaseReference | Query, _eventType?: string, callback?: unknown): void {
  const stops = listeners.get(source.path);
  if (!stops) return;
  Array.from(stops).forEach((stop) => stop());
  listeners.delete(source.path);
  void callback;
}
